import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { InfiniteData } from '@tanstack/react-query';
import { DocumentsService } from 'api';
import type { FirmDocumentMeasurementDatesResponse } from 'api';
import {
  FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_KEY,
  FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_PAGE_SIZE,
  FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_STALE_TIME_MS,
} from './useFirmDocumentMeasurementDatesQuery.constants';
import type {
  FirmDocumentMeasurementDatesQueryReturn,
  UseFirmDocumentMeasurementDatesQueryParams,
} from './useFirmDocumentMeasurementDatesQuery.types';
import { getNormalizedSearch } from '../documentsQuery.utils';

type FirmDocumentMeasurementDatesQueryKey = [
  string,
  number | null,
  number | null,
  string | null,
  string | null,
  string | null,
  string | null,
  string | null,
  string | null,
  number | null,
];

type FirmDocumentMeasurementDatesUploadedByParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsMeasurementDatesList>[5];
type FirmDocumentMeasurementDatesFileTypeParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsMeasurementDatesList>[6];
type FirmDocumentMeasurementDatesSourceParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsMeasurementDatesList>[7];
type FirmDocumentMeasurementDatesReferenceObjectTypeParam =
  Parameters<typeof DocumentsService.apiDocumentsFirmsMeasurementDatesList>[8];

const getUploadedByParam = (uploadedBy?: string | null) => {
  const uploadedById = Number(uploadedBy);

  return (uploadedById > 0 ? uploadedById : undefined) as FirmDocumentMeasurementDatesUploadedByParam;
};
const getFileTypeParam = (fileType?: string | null) => (
  fileType || undefined
) as FirmDocumentMeasurementDatesFileTypeParam;
const getSourceParam = (source?: string | null) => (
  source || undefined
) as FirmDocumentMeasurementDatesSourceParam;
const getReferenceObjectTypeParam = (referenceObjectType?: string | null) => (
  referenceObjectType || undefined
) as FirmDocumentMeasurementDatesReferenceObjectTypeParam;

export const useFirmDocumentMeasurementDatesQuery = ({
  companyId = null,
  fileType = null,
  firmId = null,
  referenceObjectType = null,
  search,
  source = null,
  targetDate = null,
  targetMeasurementDateId = null,
  uploadedBy = null,
}: UseFirmDocumentMeasurementDatesQueryParams): FirmDocumentMeasurementDatesQueryReturn => {
  const queryClient = useQueryClient();
  const normalizedSearch = getNormalizedSearch(search);
  const hasTargetMeasurementDate = Boolean(targetDate || targetMeasurementDateId);
  const noTargetQueryKeyTrimmedRef = useRef<string | null>(null);
  const queryKey = useMemo<FirmDocumentMeasurementDatesQueryKey>(() => [
    FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_KEY,
    firmId,
    companyId,
    fileType,
    referenceObjectType,
    source,
    uploadedBy,
    normalizedSearch ?? null,
    targetDate,
    targetMeasurementDateId,
  ], [
    companyId,
    fileType,
    firmId,
    normalizedSearch,
    referenceObjectType,
    source,
    targetDate,
    targetMeasurementDateId,
    uploadedBy,
  ]);
  const queryKeySignature = JSON.stringify(queryKey);
  const query = useInfiniteQuery<
    FirmDocumentMeasurementDatesResponse,
    Error,
    InfiniteData<FirmDocumentMeasurementDatesResponse>,
    FirmDocumentMeasurementDatesQueryKey,
    number | null
  >({
    enabled: Boolean(firmId),
    getNextPageParam: lastPage => (lastPage.next ? lastPage.page + 1 : undefined),
    getPreviousPageParam: firstPage => (
      firstPage.previous ? Math.max(firstPage.page - 1, 1) : undefined
    ),
    initialPageParam: hasTargetMeasurementDate ? null : 1,
    queryKey,
    queryFn: ({ pageParam }) =>
      DocumentsService.apiDocumentsFirmsMeasurementDatesList(
        String(firmId),
        pageParam ?? undefined,
        FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_PAGE_SIZE,
        companyId ?? undefined,
        normalizedSearch,
        getUploadedByParam(uploadedBy),
        getFileTypeParam(fileType),
        getSourceParam(source),
        getReferenceObjectTypeParam(referenceObjectType),
        targetDate ?? undefined,
        targetMeasurementDateId ?? undefined
      ),
    // Without a target, re-entering Documents must not refetch every persisted infinite page before we trim to page 1.
    refetchOnMount: hasTargetMeasurementDate ? undefined : false,
    staleTime: FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_STALE_TIME_MS,
  });
  // The "Most Recent" chip removes ?date, so this mode should always enter Documents on page 1.
  // Trim rehydrated infinite-query pages once per key, but do not fight user-driven "Show more" clicks.
  const shouldTrimNoTargetPages = !hasTargetMeasurementDate
    && query.data?.pages.length
    && query.data.pages.length > 1
    && noTargetQueryKeyTrimmedRef.current !== queryKeySignature;
  const pages = shouldTrimNoTargetPages
    ? query.data?.pages.slice(0, 1)
    : query.data?.pages;

  // Keep rehydrated "Most Recent" queries from retaining pages that the UI no longer intends to show.
  useEffect(() => {
    if (hasTargetMeasurementDate) {
      noTargetQueryKeyTrimmedRef.current = null;
      return;
    }

    if (!query.data || query.data.pages.length <= 1 || noTargetQueryKeyTrimmedRef.current === queryKeySignature) {
      return;
    }

    noTargetQueryKeyTrimmedRef.current = queryKeySignature;
    queryClient.setQueryData<InfiniteData<FirmDocumentMeasurementDatesResponse>>(queryKey, currentData => {
      if (!currentData || currentData.pages.length <= 1) {
        return currentData;
      }

      return {
        ...currentData,
        pageParams: currentData.pageParams.slice(0, 1),
        pages: currentData.pages.slice(0, 1),
      };
    });
  }, [
    hasTargetMeasurementDate,
    query.data,
    queryClient,
    queryKey,
    queryKeySignature,
  ]);

  const measurementDates = useMemo(
    () => pages?.flatMap(page => page.results) ?? [],
    [pages]
  );
  const totalCount = pages?.[0]?.count ?? 0;
  const firstLoadedPage = pages?.reduce(
    (firstPage, page) => Math.min(firstPage, page.page),
    Number.MAX_SAFE_INTEGER
  )
    ?? 1;
  const loadedThroughCount = pages?.reduce((maxLoadedCount, page) => {
    const pageSize = page.page_size || FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_PAGE_SIZE;
    const pageLoadedCount = ((page.page - 1) * pageSize) + page.results.length;

    return Math.max(maxLoadedCount, pageLoadedCount);
  }, 0) ?? 0;
  const loadedCount = measurementDates.length;
  const pageSize = pages?.[0]?.page_size ?? FIRM_DOCUMENT_MEASUREMENT_DATES_QUERY_PAGE_SIZE;
  const remainingNewerCount = firstLoadedPage === Number.MAX_SAFE_INTEGER
    ? 0
    : Math.max((firstLoadedPage - 1) * pageSize, 0);
  const targetFound = Boolean(pages?.[0]?.target_found);
  const targetPage = pages?.[0]?.target_page ?? null;
  const targetIndex = pages?.[0]?.target_index ?? null;

  return {
    ...query,
    loadedCount,
    measurementDates,
    remainingNewerCount,
    remainingCount: Math.max(totalCount - loadedThroughCount, 0),
    targetFound,
    targetIndex,
    targetPage,
    totalCount,
  };
};
